import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import api, { API_ENDPOINTS } from '../config/api';
import { stripHtmlTags } from '../utils/htmlUtils';
import { GIG_CATEGORIES, formatCategoryName } from '../constants/categories';
import { 
  Search, 
  Filter, 
  MapPin, 
  Clock, 
  DollarSign, 
  Users,
  Star,
  TrendingUp,
  CheckCircle,
  Calendar,
  Eye
} from 'lucide-react';

const GigsPage = () => {
  const { user, isAuthenticated } = useAuth();
  const [filters, setFilters] = useState({
    category: '',
    type: '',
    minPrice: '',
    maxPrice: '',
    search: '',
    page: 1
  });

  // Fetch gigs with current filters
  const { data: gigsData, isLoading, error } = useQuery(
    ['gigs', filters],
    () => api.get(API_ENDPOINTS.GIGS.LIST, { params: filters }),
    {
      select: (data) => data.data,
      keepPreviousData: true,
    }
  );

  const gigs = gigsData?.gigs || [];
  const totalPages = gigsData?.totalPages || 1;
  const currentPage = gigsData?.currentPage || 1;

  const categories = Object.keys(GIG_CATEGORIES);

  const gigTypes = [
    { value: '', label: 'All Types' },
    { value: 'professional', label: 'Professional Services' },
    { value: 'labor', label: 'Physical Labor' },
    { value: 'creative', label: 'Creative Work' },
    { value: 'technical', label: 'Technical Services' }
  ];

  const priceRanges = [
    { value: '', label: 'Any Price' },
    { value: '0-50', label: '$0 - $50' },
    { value: '50-200', label: '$50 - $200' },
    { value: '200-500', label: '$200 - $500' },
    { value: '500+', label: '$500+' }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const truncateTitle = (title, wordLimit = 20) => {
    if (!title) return '';
    const words = title.trim().split(/\s+/);
    if (words.length <= wordLimit) return title;
    return `${words.slice(0, wordLimit).join(' ')}...`;
  };

  const getTitleSizeClass = (title) => {
    const wordCount = (title || '').trim().split(/\s+/).filter(Boolean).length;
    if (wordCount > 14) return 'text-sm leading-snug';
    if (wordCount > 8) return 'text-base leading-snug';
    return 'text-lg leading-tight';
  };

  const getGigTypeBadgeClass = (type) => {
    if (type === 'professional') return 'bg-blue-100/95 text-blue-800 dark:bg-blue-900/80 dark:text-blue-200';
    if (type === 'labor') return 'bg-green-100/95 text-green-800 dark:bg-green-900/80 dark:text-green-200';
    if (type === 'creative') return 'bg-purple-100/95 text-purple-800 dark:bg-purple-900/80 dark:text-purple-200';
    return 'bg-orange-100/95 text-orange-800 dark:bg-orange-900/80 dark:text-orange-200';
  };

  const formatPrice = (gig) => {
    if (!gig.pricing) return 'Price negotiable';
    
    // New structure: basic/premium are objects with price property
    if (gig.pricing.basic) {
      const basicPrice = typeof gig.pricing.basic === 'object' && gig.pricing.basic.price 
        ? gig.pricing.basic.price 
        : gig.pricing.basic;
      const premiumPrice = gig.pricing.premium && typeof gig.pricing.premium === 'object' && gig.pricing.premium.price
        ? gig.pricing.premium.price
        : (typeof gig.pricing.premium === 'number' ? gig.pricing.premium : null);
      
      if (premiumPrice) {
        return `$${Number(basicPrice).toFixed(2)} - $${Number(premiumPrice).toFixed(2)}`;
      }
      return `$${Number(basicPrice).toFixed(2)}`;
    }
    
    // Legacy structure: basic/standard/premium are numbers
    if (typeof gig.pricing.basic === 'number') return `$${gig.pricing.basic.toFixed(2)}`;
    if (typeof gig.pricing.standard === 'number') return `$${gig.pricing.standard.toFixed(2)}`;
    if (typeof gig.pricing.premium === 'number') return `$${gig.pricing.premium.toFixed(2)}`;
    
    // Min/max range pricing
    if (gig.pricing.min && gig.pricing.max) {
      return `$${gig.pricing.min.toFixed(2)} - $${gig.pricing.max.toFixed(2)}`;
    }
    if (gig.pricing.min) return `$${gig.pricing.min.toFixed(2)}+`;
    
    return 'Price negotiable';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold text-heading mb-2">
          Browse Gigs
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Discover talented service providers offering their skills and expertise
        </p>
      </div>

      {/* Search and Filters */}
      <div className="card p-6 mb-6">
        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search gigs, skills, talents..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
            />
          </div>
        </div>

        {/* Filter Controls */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category
            </label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full input-field"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {GIG_CATEGORIES[cat]?.label || formatCategoryName(cat)}
                </option>
              ))}
            </select>
          </div>

          {/* Job Type Toggle - Professional or Labour */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Gig Type
            </label>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => handleFilterChange('type', filters.type === 'professional' ? '' : 'professional')}
                className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                  filters.type === 'professional'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                Professional
              </button>
              <button
                type="button"
                onClick={() => handleFilterChange('type', filters.type === 'labour' ? '' : 'labour')}
                className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                  filters.type === 'labour'
                    ? 'bg-green-600 text-white border-green-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                Labour
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Price Range
            </label>
            <select
              value={`${filters.minPrice}-${filters.maxPrice}`}
              onChange={(e) => {
                const [min, max] = e.target.value.split('-');
                handleFilterChange('minPrice', min || '');
                handleFilterChange('maxPrice', max || '');
              }}
              className="w-full input-field"
            >
              {priceRanges.map(range => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button className="btn-outline w-full">
              <Filter className="w-4 h-4 mr-2" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Gigs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {gigs.map((gig) => (
          <div
            key={gig._id}
            className="card p-0 overflow-hidden hover:shadow-lg transition-shadow duration-200 relative flex flex-col h-full"
          >
            {gig.type && (
              <span
                className={`absolute top-3 right-3 z-10 px-2.5 py-1 text-xs font-semibold rounded-full shadow-sm ${getGigTypeBadgeClass(gig.type)}`}
              >
                {gig.type.charAt(0).toUpperCase() + gig.type.slice(1)}
              </span>
            )}

            {gig.imageUrl && (
              <div className="w-full h-44 sm:h-48 overflow-hidden flex-shrink-0">
                <img
                  src={gig.imageUrl}
                  alt={gig.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="p-5 sm:p-6 flex flex-col flex-1 min-h-0">
              <Link
                to={`/gigs/${gig._id}`}
                title={gig.title}
                className={`block w-full font-semibold text-heading hover:text-accent transition-colors duration-200 mb-3 pr-16 ${getTitleSizeClass(gig.title)}`}
              >
                {truncateTitle(gig.title)}
              </Link>

              <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2 text-sm flex-shrink-0">
                {stripHtmlTags(gig.description)}
              </p>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
                <div className="flex items-center space-x-1">
                  <DollarSign className="w-4 h-4 flex-shrink-0" />
                  <span>{formatPrice(gig)}</span>
                </div>
                {gig.deliveryTime?.value && gig.deliveryTime?.unit && (
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {gig.deliveryTime.value} {gig.deliveryTime.unit}
                    </span>
                  </div>
                )}
                {gig.location && (
                  <div className="flex items-center space-x-1">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {gig.location.remote ? 'Remote' : gig.location.city || 'On-site'}
                    </span>
                  </div>
                )}
              </div>

              {gig.skills?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {gig.skills.slice(0, 4).map((skill, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs rounded-md"
                    >
                      {skill}
                    </span>
                  ))}
                  {gig.skills.length > 4 && (
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-500 text-xs rounded-md">
                      +{gig.skills.length - 4} more
                    </span>
                  )}
                </div>
              )}

              <div className="mt-auto pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-center space-x-2 min-w-0">
                  <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                    {gig.talent?.username?.[0]?.toUpperCase() || 'T'}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {gig.talent?.username}
                    </div>
                    <div className="flex items-center space-x-1 text-xs text-gray-500">
                      <Star className="w-3 h-3 fill-current text-yellow-400 flex-shrink-0" />
                      <span>{gig.talent?.stats?.rating?.average?.toFixed(1) || '0'}</span>
                      <span>({gig.talent?.stats?.rating?.count || 0})</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-2 flex-shrink-0">
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {gig.ordersCompleted || 0} orders
                  </span>
                  {isAuthenticated && user?.role === 'client' ? (
                    <Link to={`/gigs/${gig._id}`} className="btn-primary text-sm whitespace-nowrap">
                      Order Now
                    </Link>
                  ) : (
                    <Link to={`/gigs/${gig._id}`} className="btn-outline text-sm whitespace-nowrap">
                      View Details
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2 mt-8">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const page = Math.max(1, currentPage - 2) + i;
            if (page > totalPages) return null;
            
            return (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-3 py-2 text-sm rounded-lg ${
                  page === currentPage
                    ? 'bg-accent text-white'
                    : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {page}
              </button>
            );
          })}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* No Results */}
      {!isLoading && gigs.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🎨</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No gigs found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Try adjusting your search criteria or check back later for new offerings.
          </p>
          {isAuthenticated && user?.role === 'talent' && (
            <Link to="/create-gig" className="btn-primary">
              Create Your First Gig
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default GigsPage;
